/**
 * FileSelected part of bookings
 */

import { FilenameHelper } from 'store/helpers';
import {
  ApiAttachment,
  ApiAttachmentForFS,
  EachThumbnail,
  FormattedMainFile,
  MainFiles,
} from 'store/models/MainFiles';
import CommonPages, {
  CommonPagesI,
} from 'store/models/file-selected/CommonPages';
import {
  EachFileSelected,
  EachPage,
  ThumbnailMapI,
} from './file-selected.interface';

/**
 * attachments's page's pdf
 */
class AttPageMap {
  attPageMap;
  constructor(atts: ApiAttachment[]) {
    this.attPageMap = AttPageMap.attachmentsApiToAttPagelMap(atts);
  }

  static attachmentsApiToAttPagelMap(atts: ApiAttachment[]) {
    return atts.reduce(
      (acc, att) => ({
        ...acc,
        [att.partId]: att.pages.reduce(
          (accT, page) => ({
            ...accT,
            [page.num]: page,
          }),
          {},
        ),
      }),
      {},
    );
  }
  getUrlAtPartIdNum(partId: string, num: number) {
    return (
      this.attPageMap &&
      this.attPageMap[partId] &&
      this.attPageMap[partId][num] &&
      this.attPageMap[partId][num].url
    );
  }
}

class ThumbnailMap {
  static attachmentsApiToThumbnailMap(
    atts: ApiAttachment[],
  ): ThumbnailMapI {
    const attPageMapM = new AttPageMap(atts);
    return atts.reduce(
      (acc, att) => ({
        ...acc,
        [att.partId]: att.thumbnails.reduce(
          (accT, thumbnail) => ({
            ...accT,
            [thumbnail.num]: {
              ...thumbnail,
              // add pdfUrl
              pdfUrl: attPageMapM.getUrlAtPartIdNum(
                att.partId,
                thumbnail.num,
              ),
            },
          }),
          {},
        ),
      }),
      {},
    );
  }
}

class FileSelected {
  fileSelected: EachFileSelected[];
  returnModel: boolean;
  // thumbnailMap: ThumbnailMapI;
  constructor(
    fileSelectedFromBooking,
    // thumbnailMap,
    returnModel = false,
  ) {
    this.fileSelected = fileSelectedFromBooking;
    // this.thumbnailMap = thumbnailMap;
    this.returnModel = returnModel;
  }

  /**
   * 1. if each fileSelected has pages then canSelectPage is true (for it to work with the old data)
   * 2. (if attachments is passed) filter out files outside of the attachments's partIds
   * TODO: figure out how to find file.ext from EachFileSelected and set canSelectPage from that instead of check from pages length
   * @param fileSelected
   * @param attachments
   */
  static formatForInitCurBookings(
    fileSelected?: EachFileSelected[],
    attachments?: ApiAttachment[],
  ): EachFileSelected[] {
    if (!fileSelected) return [];
    let formatted = FileSelected.addCanSelectPageIfUndefined(
      fileSelected,
    );
    if (attachments) {
      const partIds = attachments.map((a) => a.partId);
      formatted = FileSelected.keepFiles(formatted, partIds);
    }

    return formatted;
  }

  /**
   * only keep the files that are in the partIds (both the root and the pages levels)
   * @param fileSelected
   * @param partIds
   */
  static keepFiles(
    fileSelected: EachFileSelected[] | undefined,
    partIds: string[],
  ): EachFileSelected[] {
    return FileSelected._keepFiles(fileSelected, partIds, false);
  }

  /**
   * remove files that are in the partIds (both the root and the pages levels)
   * @param fileSelected
   * @param partIds
   */
  static removeFiles(
    fileSelected: EachFileSelected[] | undefined,
    partIds: string[],
  ): EachFileSelected[] {
    return FileSelected._keepFiles(fileSelected, partIds, true);
  }

  private static addCanSelectPageIfUndefined(
    fileSelected: EachFileSelected[] | undefined,
  ) {
    if (!fileSelected) return [];
    // check canSelectPage just in case
    return fileSelected.map((file) => {
      if (file.canSelectPage !== undefined) return file;
      return file.pages.length > 0
        ? { ...file, canSelectPage: true }
        : file;
    });
  }

  /**
   * shared function for keepFiles and removeFiles functions
   * @param fileSelected
   * @param partIds
   * @param removePartIds
   */
  private static _keepFiles(
    fileSelected: EachFileSelected[] | undefined,
    partIds: string[],
    removePartIds = false,
  ): EachFileSelected[] {
    if (!fileSelected) return [];
    // console.debug(`formatted before`, fileSelected);

    // check canSelectPage just in case
    let formatted = FileSelected.addCanSelectPageIfUndefined(
      fileSelected,
    );

    // console.debug(`formatted after add can select page`, formatted);

    formatted = formatted.filter(
      (f) =>
        !f.partId ||
        (f.partId && // do not filter out if has partId matched but inside pages has other files
          (f.canSelectPage ||
            (removePartIds
              ? !partIds.includes(f.partId)
              : partIds.includes(f.partId)))),
    );
    // console.debug(`formatted after file`, formatted);
    // format at the pages level
    formatted = formatted.map((f) => ({
      ...f,
      pages: f.pages.filter((p) =>
        removePartIds
          ? !partIds.includes(p.partId)
          : partIds.includes(p.partId),
      ),
    }));
    // console.debug(`formatted after pages`, formatted);

    // if can select page but doesn't have any pages left -> remove file
    formatted = formatted.filter(
      (f) =>
        !f.canSelectPage || (f.canSelectPage && f.pages.length > 0),
    );
    // console.debug(`formatted after sanitize`, formatted);

    return formatted;
  }

  /**
   * auto remove file from dest if no pages
   * @param destFileIndex
   * @param pageIndex
   */
  removePage(
    destFileIndex: number,
    pageIndex: number,
  ): EachFileSelected[] | FileSelected {
    const newFileSelected = this.fileSelected
      .map((f, i) => {
        if (i !== destFileIndex) return f;
        const newF = {
          ...f,
          pages: f.pages.filter((p, ii) => ii !== pageIndex),
        };
        if (!newF.pages.length) {
          return undefined; // if no pages will remove that dest file
        }
        return newF;
      })
      .filter((f) => f !== undefined) as EachFileSelected[];
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  removeCommonPage(
    fromGlobalCommonPage: number,
  ): EachFileSelected[] | FileSelected {
    if (fromGlobalCommonPage <= 0) throw Error(`invalid page number`);
    const newFileSelected = this.fileSelected.map((f) => {
      const indexMatched = f.pages.findIndex(
        (p) => p.fromGlobalCommonPage === fromGlobalCommonPage,
      );
      if (indexMatched < 0) return f;
      else {
        const newPages = f.pages.slice(0, indexMatched).concat(
          f.pages.slice(indexMatched + 1).map((p, i) => ({
            ...p,
            fromGlobalCommonPage:
              p.fromGlobalCommonPage === undefined
                ? undefined
                : p.fromGlobalCommonPage - 1, // decrement 1
          })),
        );
        return {
          ...f,
          pages: newPages,
        };
      }
    });

    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  removeAllSelectedFiles(): EachFileSelected[] | FileSelected {
    console.debug(`before remove`, this.fileSelected);

    const newFileSelected = this.fileSelected.filter(
      (f) => f.isSelected !== true,
    );
    console.debug(`after remove`, newFileSelected);
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  removeFileAtIndex(
    index: number,
  ): EachFileSelected[] | FileSelected {
    const newFileSelected = this.fileSelected.filter(
      (f, i) => i !== index,
    );
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  isAllFileSelectedSelected(): boolean {
    if (!this.fileSelected.length) return false;
    return this.fileSelected.every((f) => f.isSelected === true);
  }

  setSelectFileAtIndex(
    destFileIndex: number,
    checked: boolean,
  ): EachFileSelected[] | FileSelected {
    const newFileSelected = this.fileSelected.map((f, i) =>
      i !== destFileIndex ? f : { ...f, isSelected: checked },
    );
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  renameFileAtIndex(
    destFileIndex: number,
    newName: string,
  ): EachFileSelected[] | FileSelected {
    const newFileSelected = this.fileSelected.map((f, i) =>
      i !== destFileIndex ? f : { ...f, filename: newName },
    );
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  setSelectFileAtAllIndexes(
    checked: boolean,
  ): EachFileSelected[] | FileSelected {
    const newFileSelected = this.fileSelected.map((f, i) => ({
      ...f,
      isSelected: checked,
    }));
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  /**
   * TODO: check about adding global common page?
   * @param srcFile
   * @param isSelectAll
   * @param pageNum
   */
  appendSrcFile(
    srcFile: ApiAttachmentForFS,
    isSelectAll: boolean,
    pageNum?: number,
  ): EachFileSelected[] | FileSelected {
    const oldNames = this.fileSelected.map((f) => f.filename);
    const newName = FilenameHelper.getUploadedFilename(
      srcFile.filename,
      oldNames,
      true,
    );
    // TODO: fix the name. check if getUploadedFilename works
    const newFileSelected = this.fileSelected.concat({
      filename: newName,
      isSelected: isSelectAll,
      canSelectPage: srcFile.canSelectPage,
      partId: srcFile.partId,
      pages:
        pageNum === undefined
          ? srcFile.thumbnails.map((thumb) => ({
              partId: srcFile.partId,
              pageNo: thumb.num,
            }))
          : [{ partId: srcFile.partId, pageNo: pageNum }],
    });
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  /**
   * TODO: check about adding global common page?
   * @param srcFiles
   * @param isSelectAll
   * @param replaceOld
   */
  appendManySrcFiles(
    srcFiles: FormattedMainFile[] | ApiAttachment[],
    isSelectAll: boolean,
    replaceOld = false, // if true then replace instead of append
  ): EachFileSelected[] | FileSelected {
    let newFileSelected = replaceOld ? [] : [...this.fileSelected];
    srcFiles.forEach((srcFile) => {
      const oldNames = newFileSelected.map((f) => f.filename);
      const newName = FilenameHelper.getUploadedFilename(
        srcFile.filename,
        oldNames,
        true,
      );
      // TODO?: fix the name. check if getUploadedFilename works
      newFileSelected.push({
        filename: newName,
        partId: srcFile.partId,
        isSelected: isSelectAll,
        canSelectPage:
          (srcFile as any).canSelectPage === undefined
            ? MainFiles.canSelectPage(srcFile.ext)
            : (srcFile as ApiAttachmentForFS).canSelectPage,
        pages: srcFile.thumbnails.map((thumb) => ({
          partId: srcFile.partId,
          pageNo: thumb.num,
        })),
      });
    });
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  /**
   * replace all files in destination
   * @param srcFiles
   * @param isSelectAll
   */
  useManySrcFiles(
    srcFiles: ApiAttachmentForFS[],
    isSelectAll: boolean,
  ) {
    return this.appendManySrcFiles(srcFiles, isSelectAll, true);
  }

  splitSrcFileAndAdd(srcFile: ApiAttachmentForFS) {
    let newFileSelected: EachFileSelected[] = [...this.fileSelected];
    srcFile.thumbnails.forEach((thumbnail: EachThumbnail) => {
      const oldFilenames = newFileSelected.map((f) => f.filename);
      const newName = FilenameHelper.getUploadedFilename(
        srcFile.filename,
        oldFilenames,
        true,
      );
      console.debug(`splitSrcFileAndAdd`, oldFilenames, newName);
      newFileSelected.push({
        filename: newName,
        canSelectPage: srcFile.canSelectPage,
        pages: [
          {
            partId: srcFile.partId,
            pageNo: thumbnail.num,
          },
        ],
      });
    });
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }
  a;

  private _addCommonPagesToPages(
    pages: EachPage[],
    commonPages: CommonPagesI,
    afterFromGlobalCommonPage = 0,
  ): EachPage[] {
    // find the first index that is not fromCommonPage -> cut there -> add the common pages -> append the rest
    let existedIndex = pages.findIndex(
      (p) => p.fromGlobalCommonPage === afterFromGlobalCommonPage,
    );

    if (existedIndex < 0) {
      existedIndex =
        pages.findIndex((p) => p.fromGlobalCommonPage === undefined) -
        1;
    }
    if (existedIndex < 0) {
      return (commonPages.map((c, i) => ({
        ...c,
        fromGlobalCommonPage: afterFromGlobalCommonPage + i + 1,
      })) as EachPage[]).concat(pages);
    } else {
      return pages
        .slice(0, existedIndex + 1)
        .concat(
          commonPages.map((c, i) => ({
            ...c,
            fromGlobalCommonPage: i + 1 + afterFromGlobalCommonPage,
          })),
        )
        .concat(pages.slice(existedIndex + 1));
    }
  }

  addCommonPagesToLastDestFile(
    commonPages: CommonPagesI,
    lastN = 1,
  ): EachFileSelected[] | FileSelected {
    if (lastN < 1) throw Error(`lastN must be at least 1`);
    const threshold = this.fileSelected.length - lastN;
    const newFileSelected = this.fileSelected.map((f, i) =>
      i >= threshold
        ? {
            ...f,
            pages: this._addCommonPagesToPages(f.pages, commonPages),
          }
        : f,
    );
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  addCommonPagesToAllDestFiles(
    commonPages: CommonPagesI,
    afterFromGlobalCommonPage?: number, // after
  ): EachFileSelected[] | FileSelected {
    const newFileSelected = this.fileSelected.map((f) =>
      f.canSelectPage === false
        ? f // not prepend page if file is not processed
        : {
            ...f,
            pages: this._addCommonPagesToPages(
              f.pages,
              commonPages,
              afterFromGlobalCommonPage,
            ),
          },
    );
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  addCommonPagesToAllDestFilesWithSrcFile(
    srcFile: FormattedMainFile,
    srcPageNo?: number, // if no srcPageNo passed means all pages
    afterFromGlobalCommonPage?: number, // after
  ): EachFileSelected[] | FileSelected {
    const commonPages = CommonPages.getCommonPagesFromSrcFilePageNum(
      srcFile,
      srcPageNo,
    );

    return this.addCommonPagesToAllDestFiles(
      commonPages,
      afterFromGlobalCommonPage,
    );
  }
  private _addCommonPageToPagesAfterFromGlobalCommonPage(
    pages: EachPage[],
    commonPage: EachPage,
    fromGlobalCommonPage?: number,
  ): EachPage[] {
    console.debug(
      `_addCommonPageToPagesAfterFromGlobalCommonPage`,
      fromGlobalCommonPage,
      commonPage,
    );
    if (fromGlobalCommonPage === undefined) {
      // TODO: refactor
      return [commonPage].concat(
        FileSelected.shiftFromGlobalCommonPage(pages),
      );
    } else {
      let existedIndex = pages.findIndex(
        (p) => p.fromGlobalCommonPage === fromGlobalCommonPage,
      );

      if (existedIndex < 0) {
        existedIndex =
          pages.findIndex(
            (p) => p.fromGlobalCommonPage === undefined,
          ) - 1;
      }

      if (existedIndex < 0) {
        // TODO: refactor
        return [commonPage].concat(
          FileSelected.shiftFromGlobalCommonPage(pages),
        );
      }

      return pages
        .slice(0, existedIndex + 1)
        .concat(commonPage)
        .concat(
          FileSelected.shiftFromGlobalCommonPage(
            pages.slice(existedIndex + 1),
          ),
        );
    }
  }
  static shiftFromGlobalCommonPage(
    pages: EachPage[],
    incr = 1,
  ): EachPage[] {
    return pages.map((p, i) => ({
      ...p,
      fromGlobalCommonPage:
        p.fromGlobalCommonPage === undefined
          ? undefined
          : p.fromGlobalCommonPage + incr, // TODO: explain this number
    }));
  }
  addCommonPageAfterFromGlobalCommonPageToAllDestFiles(
    srcPartId: string,
    srcPageNum: number, // one page
    fromGlobalCommonPage?: number,
  ): EachFileSelected[] | FileSelected {
    const newFileSelected = this.fileSelected.map((f) =>
      f.canSelectPage === false
        ? f // not prepend page if file is not processed
        : {
            ...f,
            pages: this._addCommonPageToPagesAfterFromGlobalCommonPage(
              f.pages,
              {
                partId: srcPartId,
                pageNo: srcPageNum,
                fromGlobalCommonPage: (fromGlobalCommonPage || 0) + 1,
              },
              fromGlobalCommonPage,
            ),
          },
    );
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  clearCommonPages(): EachFileSelected[] | FileSelected {
    const newFileSelected = this.fileSelected.map((f) => ({
      ...f,
      pages: f.pages.filter(
        (p) => p.fromGlobalCommonPage === undefined,
      ),
    }));
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  prependPagesToAllDestFiles(
    srcFile: FormattedMainFile,
    srcPageNo?: number, // if no srcPageNo passed means all pages
  ): EachFileSelected[] | FileSelected {
    const newFileSelected = this.fileSelected.map((f) =>
      f.canSelectPage === false
        ? f // not prepend page if file is not processed
        : {
            ...f,
            pages: [
              ...(srcPageNo // prepend part
                ? [srcPageNo]
                : srcFile.thumbnails.map((t) => t.num)
              ).map((pageNo) => ({
                partId: srcFile.partId,
                pageNo,
              })),
              ...f.pages,
            ],
          },
    );
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  appendSrcFileToAttIndexes(
    srcAtt: ApiAttachmentForFS,
    destFileIndexes: number[],
  ): EachFileSelected[] | FileSelected {
    if (srcAtt.canSelectPage) {
      const newPagesItems = srcAtt.pages.map((p) => ({
        partId: srcAtt.partId,
        pageNo: p.num,
      }));
      const newFileSelected = this.fileSelected.map((f, i) =>
        !destFileIndexes.includes(i)
          ? f
          : {
              ...f,
              pages: f.pages.concat(newPagesItems),
            },
      );
      this.fileSelected = newFileSelected;
    } else {
      console.warn(`cannot add to !canSelectPage srcAtt`);
    }
    return this.returnModel ? this : this.fileSelected;
  }

  appendPageToAttIndexes(
    srcAtt,
    srcPageNo,
    destFileIndexes: number[],
  ): EachFileSelected[] | FileSelected {
    const newPageItem = {
      partId: srcAtt.partId,
      pageNo: srcPageNo,
    };
    const newFileSelected = this.fileSelected.map((f, i) =>
      !destFileIndexes.includes(i)
        ? f
        : {
            ...f,
            pages: f.pages.concat(newPageItem),
          },
    );
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  appendPageToAttIndex(
    srcAtt,
    srcPageNo,
    destFileIndex?: number, // if undefined will add to the last file
  ): EachFileSelected[] | FileSelected {
    return this.addPageToAttIndexAfterPageIndex(
      srcAtt,
      srcPageNo,
      destFileIndex,
    );
  }

  addPageToAttIndexAfterPageIndex(
    srcAtt,
    srcPageNo,
    destFileIndex?: number, // if undefined will add to the last file
    pageIndex?: number, // where the page it should be after. if undefined will add to the last page.
  ): EachFileSelected[] | FileSelected {
    if (srcPageNo < 1) throw Error(`pateNo must be >= 1`);
    const newPageItem = {
      partId: srcAtt.partId,
      pageNo: srcPageNo,
    };
    let newFileSelected: EachFileSelected[];
    console.debug(`this.fileSelected`, this.fileSelected);
    if (destFileIndex === undefined) {
      // add to the last file when destFileIndex is undefined
      destFileIndex = this.fileSelected.length - 1;
    }
    if (destFileIndex <= this.fileSelected.length - 1) {
      newFileSelected = this.fileSelected.map((f, i) =>
        i !== destFileIndex
          ? f
          : {
              ...f,
              pages:
                pageIndex === undefined ||
                pageIndex > f.pages.length - 1
                  ? f.pages.concat(newPageItem)
                  : f.pages
                      .slice(0, pageIndex + 1)
                      .concat(newPageItem)
                      .concat(f.pages.slice(pageIndex + 1)),
            },
      );
    } else {
      newFileSelected = this.fileSelected.concat({
        filename: srcAtt.filename, // TODO: might have to add suffix if there are duplicated filename
        canSelectPage: srcAtt.canSelectPage,
        pages: [newPageItem],
      });
    }
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  moveCommonPage(
    fromFromGlobalPage: number,
    toFromGlobalPage: number,
  ) {
    const newFileSelected = this.fileSelected.map((f) => {
      const fromIndex = f.pages.findIndex(
        (p) => p.fromGlobalCommonPage === fromFromGlobalPage,
      );
      const toIndex = f.pages.findIndex(
        (p) => p.fromGlobalCommonPage === toFromGlobalPage,
      );

      if (fromIndex < 0 && toIndex < 0) {
        // TODO: reset fromGlobalCommonPage
        return f;
      } else if (fromIndex < 0 && toIndex >= 0) {
        return {
          ...f,
          pages: f.pages.map((p, i) =>
            i !== toIndex
              ? p
              : { ...p, fromGlobalCommonPage: fromFromGlobalPage },
          ),
        };
      } else if (fromIndex >= 0 && toIndex < 0) {
        return {
          ...f,
          pages: f.pages.map((p, i) =>
            i !== fromIndex
              ? p
              : { ...p, fromGlobalCommonPage: toFromGlobalPage },
          ),
        };
      } else {
        const pages = [...f.pages];
        const oldFrom = pages[fromIndex];
        pages[fromIndex] = {
          ...pages[toIndex],
          fromGlobalCommonPage: fromFromGlobalPage,
        };
        pages[toIndex] = {
          ...oldFrom,
          fromGlobalCommonPage: toFromGlobalPage,
        };
        return { ...f, pages };
      }
    });
    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  /**
   * TODO: write test
   * @param destOldFileIndex
   * @param oldPageIndex
   * @param destNewFileIndex
   * @param insertAfterPageIndex
   * @returns
   */
  movePage(
    destOldFileIndex,
    oldPageIndex,
    destNewFileIndex,
    insertAfterPageIndex,
  ) {
    let newFileSelected;
    // insert to the same place
    if (
      destOldFileIndex === destNewFileIndex &&
      oldPageIndex === insertAfterPageIndex
    ) {
      newFileSelected = this.fileSelected;
    } else {
      const destOldFile = this.fileSelected.find(
        (f, i) => i === destOldFileIndex,
      );
      if (!destOldFile) throw Error(`couldn't find the file`);
      const destOldPage = destOldFile?.pages.find(
        (p, i) => i === oldPageIndex,
      ) as EachPage;
      if (!destOldPage) throw Error(`couldn't find the page`);

      const afterRemovePageM = new FileSelected(
        this.fileSelected,
        true,
      ).removePage(destOldFileIndex, oldPageIndex) as FileSelected;

      newFileSelected = afterRemovePageM.fileSelected.map((f, i) =>
        i !== destNewFileIndex
          ? f
          : {
              ...f,
              pages: this._insertOldPageAfterIndex(
                f.pages,
                insertAfterPageIndex,
                destOldPage,
                oldPageIndex,
                destOldFileIndex === destNewFileIndex,
              ),
            },
      );
    }

    this.fileSelected = newFileSelected;
    return this.returnModel ? this : this.fileSelected;
  }

  private _insertOldPageAfterIndex(
    pages: EachPage[],
    insertAfterPageIndex: number,
    destOldPage: EachPage,
    oldPageIndex: number,
    isSameFile: boolean,
  ): EachPage[] {
    if (insertAfterPageIndex < 0) {
      return [destOldPage].concat(pages);
    } else if (!isSameFile || oldPageIndex > insertAfterPageIndex) {
      return (pages as EachPage[]).reduce((acc, p, i) => {
        if (p) acc.push(p);
        if (i === insertAfterPageIndex) {
          acc.push(destOldPage);
        }
        return acc;
      }, [] as EachPage[]);
    } else {
      // left to right for the same file
      return (pages as EachPage[]).reduce((acc, p, i) => {
        if (p) acc.push(p);
        if (i === insertAfterPageIndex - 1) {
          acc.push(destOldPage);
        }
        return acc;
      }, [] as EachPage[]);
    }
  }
}

export { FileSelected, ThumbnailMap };
